import { useOnceEffect } from "@core/hooks/use-once-effect";
import { useI18n } from "@core/i18n/i18n";
import { getStartedManager, programOperationsManager } from "@core/service-container";
import { Patient, PendingPatient } from "@domain/patient/patient";
import { DatedPatientProgram, PatientProgramEditor } from "@domain/program/patient-program";
import { getProgramDetails, isNewProgram, NewProgram, Program, ProgramContent } from "@domain/program/program";
import { sendAnalyticsEventEditProgram, sendAnalyticsEventStartProgram } from "@domain/program/program-analytics";
import { ProgramNameTooLong } from "@domain/program/program-error";
import { SessionDoneContentChangedError, SessionOrderChangedError } from "@domain/program/program-errors";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { ButtonProps } from "@ui/components/buttons/button";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ConfirmActionDialog } from "@ui/pages/program-builder/dialog/confirm-dialog";
import { ScheduleProgramDialog } from "@ui/pages/program-builder/dialog/schedule-dialog/schedule-program-dialog";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useMemo, useState } from "react";

export interface StartProgramDialogProps extends DialogProps {
	patient: Patient | PendingPatient;
	editedProgram: ProgramContent;
	program: Program | NewProgram;
	editor: PatientProgramEditor;
	onSuccess?: () => void;
	skipDialogConfirmation?: boolean;
	handleLoading?: (isLoading: boolean) => void;
}

interface ConfirmModeWithData {
	data: ProgramContent;
	description: string;
	confirmButtonLabel?: string;
	confirmButtonStyle?: ButtonProps["uiStyle"];
}

export const StartProgramDialog: React.FC<StartProgramDialogProps> = ({
	patient,
	editor,
	editedProgram,
	program,
	onSuccess,
	skipDialogConfirmation,
	handleLoading,
	...otherProps
}) => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [confirmModeWithData, setConfirmModeWithData] = useState<ConfirmModeWithData | null>(null);
	const newProgram = useMemo(() => ({ ...program, ...editedProgram }), [editedProgram, program]);

	const saveAndStart = useCallback(
		async (data: ProgramContent, forceChanges: boolean) => {
			try {
				handleLoading?.(true);
				setLoadingSubmit(true);
				const startedProgram = await saveAndStartProgramOperation(patient.id, data, newProgram, editor, forceChanges);
				await getStartedManager.tryMarkProgramsStepAsCompleted();
				sendAnalytics(patient, program, { ...newProgram, ...data }, startedProgram);

				if (skipDialogConfirmation) {
					showToast(
						format("programBuilder.updateFutureProgramDialog.successToast.title"),
						format("programBuilder.updateFutureProgramDialog.successToast.message", {
							program: newProgram.name,
						}),
						"success",
					);
				} else {
					showToast(
						format("programBuilder.startDialog.successToast.title"),
						format("programBuilder.startDialog.successToast.message"),
						"success",
					);
				}

				otherProps.onHide();
				onSuccess?.();
			} catch (e) {
				if (e instanceof ProgramNameTooLong) {
					showToast(
						format("programBuilder.startDialog.errorToast.title"),
						format("programBuilder.startDialog.errorToast.nameTooLongMessage"),
					);
					return;
				}
				if (e instanceof SessionDoneContentChangedError) {
					setConfirmModeWithData({
						data,
						description: format(
							"programBuilder.editOngoingProgramDialog.confirmDialog.sessionDoneContentChanged.message",
						),
					});
					return;
				}
				if (e instanceof SessionOrderChangedError) {
					setConfirmModeWithData({
						data,
						description: format("programBuilder.editOngoingProgramDialog.confirmDialog.sessionOrderChanged.message"),
						confirmButtonLabel: format(
							"programBuilder.editOngoingProgramDialog.confirmDialog.sessionOrderChanged.confirmButtonLabel",
						),
						confirmButtonStyle: "primary",
					});
					return;
				}
				console.error(e);
				setConfirmModeWithData(null);
				showToast(
					format("programBuilder.startDialog.errorToast.title"),
					format("programBuilder.startDialog.errorToast.message"),
				);
			} finally {
				setLoadingSubmit(false);
				handleLoading?.(false);
			}
		},
		[
			handleLoading,
			newProgram,
			editor,
			patient,
			program,
			skipDialogConfirmation,
			otherProps,
			onSuccess,
			showToast,
			format,
		],
	);

	useOnceEffect(() => saveAndStart(editedProgram, false), skipDialogConfirmation, [
		editedProgram,
		saveAndStart,
		skipDialogConfirmation,
	]);

	if (confirmModeWithData) {
		return (
			<ConfirmActionDialog
				{...otherProps}
				title={format("programBuilder.editOngoingProgramDialog.confirmDialog.title.v2_0")}
				message={confirmModeWithData.description}
				confirmButtonLabel={confirmModeWithData.confirmButtonLabel}
				confirmLoading={loadingSubmit}
				onCancel={() => setConfirmModeWithData(null)}
				onHide={() => setConfirmModeWithData(null)}
				onConfirm={() => saveAndStart(confirmModeWithData.data, true)}
			/>
		);
	}

	if (skipDialogConfirmation) {
		return <></>;
	}

	return (
		<ScheduleProgramDialog
			patient={patient}
			program={newProgram}
			editedProgram={editedProgram}
			onSave={data => saveAndStart({ ...editedProgram, ...data } as ProgramContent, false)}
			{...otherProps}
		/>
	);
};

async function saveAndStartProgramOperation(
	patientId: string,
	programContent: ProgramContent,
	currentProgram: Program | NewProgram,
	editor: PatientProgramEditor,
	forceChanges = false,
): Promise<DatedPatientProgram> {
	let startedProgram: DatedPatientProgram;
	if (currentProgram.status === ProgramStatus.Ongoing) {
		startedProgram = await programOperationsManager.performUpdateOngoingOperation(
			patientId,
			currentProgram.id,
			programContent,
			editor,
			forceChanges,
		);
	} else {
		startedProgram = await programOperationsManager.performStartOperation(
			patientId,
			programContent,
			currentProgram.status,
			editor,
			isNewProgram(currentProgram) ? undefined : currentProgram.id,
		);
	}
	return startedProgram;
}

function sendAnalytics(
	patient: Patient | PendingPatient | undefined,
	previousProgram: Program | NewProgram,
	newProgram: Program | NewProgram,
	startedProgram: DatedPatientProgram,
) {
	const previousStatus = previousProgram.status;
	if (previousStatus === ProgramStatus.Scheduled || previousStatus === ProgramStatus.Ongoing) {
		sendAnalyticsEventEditProgram([
			patient?.analyticsId ?? "",
			startedProgram.id,
			previousStatus === ProgramStatus.Ongoing ? "ongoing" : "planned",
			getProgramChangesLabel(previousProgram, newProgram).join(";"),
		]);
	} else {
		const exerciseDetails = getProgramDetails(newProgram.days);
		sendAnalyticsEventStartProgram(patient?.analyticsId ?? "", startedProgram, exerciseDetails, patient?.status);
	}
}

function getProgramChangesLabel(previousProgram: Program, newProgram: Program | NewProgram) {
	const changes = [
		newProgram.endDate !== previousProgram.endDate ? "durationChanged" : "",
		!newProgram.days.equals(previousProgram.days) ? "sessionChanged" : "",
		newProgram.name !== previousProgram.name ? "titleChanged" : "",
	];
	return changes.filter(change => !!change);
}
